import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import crossIcon from "../../assets/Icons/crossIcon.svg";
import { setQuery } from "../../store/filters";
import "./AppliedFilterChips.scss";

/* What is currently filtering the page, as removable chips.

   Lives here rather than in ActionHeader because the dashboard has its own
   header and needs to say the same thing: the filter dropdown alone tells
   you a filter exists, not which one.

   Selecting a dozen months would render a dozen chips in one unbounded row,
   so each group shows a few and keeps the rest behind "+N more", and the row
   itself stays on one line and scrolls sideways. */
const CHIP_LIMIT = 5;

const DEFAULT_MONTHS = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

/* the dates are shown by the range picker in the header itself */
const SKIPPED_KEYS = ["date_from", "date_to"];

const GROUP_LABELS = {
  trust_id: "Portfolio",
  portfolios: "Portfolio",
  team_member: "Team member",
  team_leader: "Team leader",
  action_code: "Action code",
  loan_type: "Loan type",
  states: "State",
  state: "State",
  months: "Months",
  region: "Region",
  status: "Status",
  disposition: "Disposition",
  agency: "Agency",
};

const groupLabel = (key) => GROUP_LABELS[key] || String(key).replace(/_/g, " ");

/* A name has to actually say something: these lists have come back with "-"
   for a name, and an empty string or a bare dash on a chip is
   indistinguishable from a rendering bug. Anything blank-or-dash falls
   through to the next label, and finally to the id, which is at least real
   and lets the chip be removed. */
const isMeaningful = (value) =>
  typeof value === "string" && value.trim() !== "" && value.trim() !== "-";

const chipName = (record, fallback) =>
  [record?.name, record?.key, record?.label, record?.email].find(
    isMeaningful,
  ) || fallback;

const chipLabel = (text) => {
  if (text === undefined || text === null || text === "") return null;
  const value = String(text);
  if (value.length <= 12) return value;
  return <Tooltip title={value}>{value.slice(0, 11) + "…"}</Tooltip>;
};

/* The row scrolls sideways rather than wrapping, and a platform scrollbar
   under it just looked like a scrollbar. These are the controls the scroll
   actually needs: a pair of nudge buttons that appear only when there is
   somewhere to go, an edge that fades into the overflow, and a vertical
   wheel over the row mapped to horizontal movement. */
const FilterChipBar = ({ children }) => {
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;

    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      const next = {
        start: el.scrollLeft > 1,
        end: max > 1 && el.scrollLeft < max - 1,
      };
      /* scroll fires continuously; only a change of state is worth a render */
      setEdges((prev) =>
        prev.start === next.start && prev.end === next.end ? prev : next,
      );
    };

    /* React's own wheel listener is passive, so this one is bound here where
       it can preventDefault */
    const onWheel = (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) return;
      const next = el.scrollLeft + event.deltaY;
      if (next < 0 || next > max) return; // let the page have it at the ends
      event.preventDefault();
      el.scrollLeft = next;
    };

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", measure);

    /* chips come and go as filters are applied and groups expanded */
    const observer = new MutationObserver(measure);
    observer.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener("scroll", measure);
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, []);

  const nudge = (direction) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: direction * 260, behavior: "smooth" });
  };

  return (
    <div
      className={[
        "selected-filter-bar",
        edges.start ? "can-start" : "",
        edges.end ? "can-end" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="selected-filter-itmes" ref={scroller}>
        {children}
      </div>
      {(edges.start || edges.end) && (
        <div className="chip-nav">
          <button
            type="button"
            onClick={() => nudge(-1)}
            disabled={!edges.start}
            aria-label="Scroll filters left"
          >
            <LeftOutlined />
          </button>
          <button
            type="button"
            onClick={() => nudge(1)}
            disabled={!edges.end}
            aria-label="Scroll filters right"
          >
            <RightOutlined />
          </button>
        </div>
      )}
    </div>
  );
};

const FilterChipGroup = ({ label, items = [], onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const hidden = Math.max(items.length - CHIP_LIMIT, 0);
  const shown = expanded ? items : items.slice(0, CHIP_LIMIT);

  return (
    <div className="selected-secondary-wrapper">
      <div className="selected-secondary-item">{label}</div>
      <div className="childrens">
        {shown.map((chip) => (
          <div key={chip.key}>
            {chipLabel(chip.name)}
            <img src={crossIcon} alt="" onClick={() => onRemove?.(chip.key)} />
          </div>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            className="chip-more"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Show less" : `+${hidden} more`}
          </button>
        )}
      </div>
    </div>
  );
};

const AppliedFilterChips = ({ leading = null, onRemove }) => {
  const dispatch = useDispatch();
  const { query, analyse_filters, filter_list } = useSelector(
    (state) => state.filters,
  );
  const { portfolios } = useSelector((state) => state.portfolio);

  /* Both filter sources are searched by key: which one holds a category
     depends on the screen, and the values are ids for object categories and
     plain strings for the rest. */
  const resolve = (key, value) => {
    if (key === "trust_id") {
      return chipName(
        portfolios?.find((portfolio) => portfolio?.id === value),
        value,
      );
    }
    const source = [...(analyse_filters || []), ...(filter_list || [])].find(
      (filter) => filter?.name === key,
    )?.filters;
    let record = Array.isArray(source)
      ? source.find(
          (entry) =>
            entry?.id === value ||
            String(entry?.id) === String(value) ||
            entry === value,
        )
      : null;
    if (!record && key === "months") {
      record = DEFAULT_MONTHS.find(
        (m) => m.id === value || String(m.id) === String(value),
      );
    }
    return chipName(record, value);
  };

  const removeChip = (key, value) => {
    if (onRemove) {
      onRemove(key, value);
      return;
    }
    const remaining = (query?.[key] || []).filter(
      (entry) => entry !== value && String(entry) !== String(value),
    );
    dispatch(setQuery({ ...query, [key]: remaining }));
  };

  const groups = Object.keys(query || {})
    .filter(
      (key) =>
        !SKIPPED_KEYS.includes(key) &&
        Array.isArray(query[key]) &&
        query[key].length > 0,
    )
    .map((key) => (
      <FilterChipGroup
        key={key}
        label={groupLabel(key)}
        items={query[key].map((value) => ({
          key: value,
          name: resolve(key, value),
        }))}
        onRemove={(value) => removeChip(key, value)}
      />
    ));

  /* nothing selected and nothing to lead with - no empty strip */
  if (!leading && groups.length === 0) return null;

  return (
    <FilterChipBar>
      {leading}
      {groups}
    </FilterChipBar>
  );
};

export default AppliedFilterChips;
